import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url
});


interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    orderView?: InjectableCss;
    orderDateLabel?: InjectableCss;
    orderDateValue?: InjectableCss;
    orderDateWrapper?: InjectableCss;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
            flex-direction: column;
        `,
    },
    container: {
        gap: 5,
        css: css`
            background-color: #fff;
            padding: .688rem .938rem;
            box-shadow: 0 .125rem .75rem 0 rgba(0,0,0,0.04);
            justify-content: space-around;
            text-align: justify;
            position: relative;

            @media screen and (min-width: 1199px) {
                > div {
                    max-width: 54.875rem;
                }
            }
            
            p[data-test-selector="heading"] {
                color: #005CB9;
                font-family: 'Roboto', sans-serif;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .81px;
                line-height: 1rem;
                white-space: nowrap;
                text-transform: none;
            }

            p[data-test-selector="text"] {
                color: #2E384D;
                font-family: 'Lato',sans-serif;
                font-size: .875rem;
                letter-spacing: .5px;
                line-height: 1.375rem;
                white-space: normal;
                word-break: break-word;
            }

            > div {
                justify-content: flex-start;
                text-align: justify;
            }

            @media screen and (min-width: 768px) and (max-width: 1024px) {
                p[data-test-selector="heading"] {
                    font-size: .813rem;
                    letter-spacing: .5px;
                }
    
                p[data-test-selector="text"] {
                    font-size: .813rem;
                    letter-spacing: .2px;
                }
            }

            @media screen and (max-width: 1279px) {
                > div {
                    width: calc(100% + 0.313rem);
                }
                p[data-test-selector="heading"] {
                    display: inline-flex;
                    line-height: 1.375rem;
                }
    
                p[data-test-selector="text"] {
                    display: inline-flex;
                    justify-content: flex-end;
                    padding-left: .625rem;
                    text-align: right;
                }
            }

            @media screen and (min-width: 1024px) {
                padding: 0.688rem 1.938rem;
            }

        }
        `,
    },
    quoteDetailsLinkGridItem: {
        css: css`
            margin: 0 auto;
            min-width: 5.938rem;
            @media screen and (min-width: 1280px) {
                min-width: 91px;
            }
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                    width: 100%;
                    justify-content: space-between;
                    display: flex;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    typeGridItem: { 
        css: css`
            margin: 0 auto;
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
        `,
    },
    statusGridItem: { 
        css: css`
            margin: 0 auto;
            min-width: 7.813rem;
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    orderDateGridItem: { 
        css: css`
            margin: 0 auto;
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    expirationDateGridItem: { 
        css: css`
            margin: 0 auto;
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    salesRepGridItem: { 
        css: css`
            margin: 0 auto;
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    userNameGridItem: { 
        css: css`
            margin: 0 auto;
            text-transform: capitalize;

            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    customerGridItem: { 
        css: css`
            margin: 0 auto;
            min-width: 8.438rem;
            text-transform: capitalize;

            @media screen and (min-width: 1280px) {
                min-width: 10.438rem;
                text-align: left;
            }
            @media screen and (max-width: 1279px) {
                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    shipToAddressGridItem: { 
        css: css`
            margin: 0 auto;
            min-width: 7rem;
            text-transform: capitalize;

            @media screen and (min-width: 1280px) {
                text-align: left;
            }

            @media screen and (max-width: 1279px) {
                p[data-test-selector="text"] {
                    text-align: right;
                }

                > div {
                    flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    orderDateLabel: {
        css: css`
            color: #333F48;
            font-family: 'Roboto', sans-serif;
            font-size: .875rem;
            letter-spacing: 1.31px;
            line-height: 1rem;
            display: inline-flex;
            margin-right: 1.125rem;
            vertical-align: middle;
            justify-content: center;
            align-items: center;
            letter-spacing: 0;
        `,
    },
    orderDateValue: {
        css: css`
            color: #002554 !important;
            font-family: 'Lato', sans-serif;
            font-size: 1rem;
            font-weight: bold;
            letter-spacing: .57px;
            line-height: 1.375rem;
            margin-left: 0;

            a > span {
                color: #002554 !important;
                font-family: 'Lato', sans-serif;
                font-size: 1rem;
                font-weight: bold;
                letter-spacing: .57px;
                line-height: 1.375rem;
                margin-left: 0;
            }
        `,
    },
    orderDateWrapper: {
        css: css`
            color: #535E64;
            background-color: #E5EEF7;
            width: 100%;
            padding: .75rem .75rem .313rem 1.25rem;

            a > span {
                color: #002554 !important;
                font-family: 'Lato', sans-serif;
                font-size: 1rem;
                font-weight: bold;
                letter-spacing: .57px;
                line-height: 1.375rem;
            }
        `,
    },
    orderView: {
        css: css`
            padding: 0 .938rem;
            position: relative;
            text-align: center;
            align-items: center;
            justify-content: center;

            &:after {
                content: '';
                display: block;
                height: 1.875rem;
                width: .063rem;
                position: absolute;
                right: .938rem;
                border-right: .063rem solid #E0E0E0;
            }

            @media screen and (min-width: 1280px) {
                text-align: -webkit-left;
                justify-content: flex-start;
                margin-left: 0 !important;
                padding-left: 0 !important;
                min-width: 35px;
                max-width: 35px;

                &:after {
                    right: auto;
                    left: 2.5rem;
                }
            }

            @media screen and (max-width: 1279px) {
                width: 3.75rem;
                position: absolute;
                height: 100%;
                max-width: 3.75rem;
                flex-basis: 3.75rem;
                left: 1.563rem;
                top: 1.25rem;
                align-items: flex-start;
                padding-left: 0;
                justify-content: flex-start;

                &:after {
                    position: relative;
                    height: 85%;
                    left: 1.563rem;
                    top: -0.25rem;
                }
            }
        `,
    }
};

const QuoteSummaryCard: FC<Props> = ({ rfqQuoteDetailsPageUrl,quote, session, quoteSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            },
        });
    }
    
    const customerDisplay = `${quote.customerName}`;
    return (
        <GridItem {...styles.quoteSummaryCardGridItem} data-test-selector="recentQuotes">
            <Typography as="div" {...styles.orderDateWrapper} data-test-selector="orderSummaryCard_Date">
                <Typography as="span" {...styles.orderDateLabel}>{translate("Date Requested")}</Typography>
                <Typography as="span" {...styles.orderDateValue}>{orderDateDisplay}</Typography>
            </Typography>
            <GridContainer {...styles.container}>
                <GridItem {...styles.orderView}>
                    <Link href={`${rfqQuoteDetailsPageUrl}?quoteId=${quote.id}`}>
                        <Icon src="Eye" size={24} />
                    </Link>
                </GridItem>
                <GridItem {...styles.quoteDetailsLinkGridItem}>
                    <Typography  as="div">
                        <Typography as="p" data-test-selector="heading">{translate("Quote #")}</Typography> 
                        <Typography as="p" data-test-selector="text">{quote.quoteNumber}</Typography>
                    </Typography>
                </GridItem>
                {quoteSettings.jobQuoteEnabled && (
                    <GridItem {...styles.typeGridItem}>
                        <SmallHeadingAndText
                            heading={translate("Type")}
                            text={quote.typeDisplay}
                            extendedStyles={styles.typeHeadingAndText}
                        />
                    </GridItem>
                )}
                <GridItem {...styles.statusGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Status")}
                        text={quote.statusDisplay}
                        extendedStyles={styles.statusHeadingAndText}
                    />
                </GridItem>
                {!session.isSalesPerson && (
                    <GridItem {...styles.shipToAddressGridItem}>
                        <SmallHeadingAndText
                            heading={translate("Ship to ")}
                            text={quote.customerName.toLowerCase()}
                            extendedStyles={styles.shipToAddressHeadingAndText}
                        />
                    </GridItem>
                )}
                {expirationDateDisplay && (
                    <GridItem {...styles.expirationDateGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Expires")}
                        text={expirationDateDisplay}
                        extendedStyles={styles.expirationDateHeadingAndText}
                    />
                    </GridItem>
                )}
                {session.isSalesPerson && (
                    <>
                        <GridItem {...styles.salesRepGridItem}>
                            <SmallHeadingAndText
                                heading={translate("Sales Rep")}
                                text={quote.salespersonName}
                                extendedStyles={styles.salesRepHeadingAndText}
                            />
                        </GridItem>
                        <GridItem {...styles.userNameGridItem}>
                            <SmallHeadingAndText
                                heading={translate("User")}
                                text={quote.userName.toLowerCase()}
                                extendedStyles={styles.userNameHeadingAndText}
                            />
                        </GridItem>
                    </>
                )}
                <GridItem {...styles.customerGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Customer")}
                        text={customerDisplay.toLowerCase()}
                        extendedStyles={styles.customerHeadingAndText}
                    />
                </GridItem>
                
            </GridContainer>
        </GridItem>
    );
};

export default connect(mapStateToProps)(QuoteSummaryCard);

